// types
import { IFieldOption } from 'types/graphql'
// utils
import { v4 as uuid } from 'uuid'
import { rowsToCsv, toCellString } from 'utils/csv'

const CSV_HEADER = ['Label', 'Value', 'Active']
const HEADER_MATCHERS = [/^label$/i, /^value$/i]

const isHeaderRow = (row: unknown[]): boolean =>
  HEADER_MATCHERS.every((matcher, index) => matcher.test(toCellString(row[index])))

const toActive = (value: unknown): boolean => toCellString(value).toLowerCase() === 'true'

export interface IParsedOptions {
  items: Omit<IFieldOption, 'id'>[]
  skipped: number
}

/** Rows are `[Label, Value, Active]`; rows without label or value and repeated values are skipped. */
export const parseRowsToOptions = (rows: unknown[][]): IParsedOptions => {
  const dataRows = rows.filter((row, index) => !(index === 0 && isHeaderRow(row)))

  const usedValues = new Set<string>()

  const items = dataRows.reduce<IParsedOptions['items']>((acc, row) => {
    const label = toCellString(row[0])
    const value = toCellString(row[1])

    if (!label || !value || usedValues.has(value)) {
      return acc
    }

    usedValues.add(value)

    return [...acc, { label, value, active: toActive(row[2]) }]
  }, [])

  return { items, skipped: dataRows.length - items.length }
}

/**
 * Existing options are deactivated; an existing option with the same value as an imported one
 * is updated in place (keeps its id), the rest of the imported options are appended.
 */
export const mergeImportedOptions = (options: IFieldOption[], items: IParsedOptions['items']): IFieldOption[] => {
  const importedByValue = new Map(items.map((item) => [item.value, item]))
  const existingValues = new Set(options.map(({ value }) => value))

  const updatedOptions = options.map((option) => {
    const imported = importedByValue.get(option.value)

    return imported ? { ...option, ...imported } : { ...option, active: false }
  })

  const newOptions = items.filter(({ value }) => !existingValues.has(value)).map((item) => ({ id: uuid(), ...item }))

  return [...updatedOptions, ...newOptions]
}

export const optionsToCsv = (options: IFieldOption[]): string =>
  rowsToCsv([CSV_HEADER, ...options.map(({ label, value, active }) => [label, value, active ? 'TRUE' : 'FALSE'])])
